import { type FC, useCallback, useMemo, useRef, useState } from "react";

import Spinner from "~/common/components/Spinner/Spinner";
import OrderbookSearchBox, {
  type OrderbookSearchBoxData,
  type OrderbookSearchBoxHandle,
} from "~/features/orderbook/components/OrderbookSearchBox/OrderbookSearchBox";
import { type OrderbookRowItem, type orderColumnPreferences } from "~/features/orderbook/models/orderbookModels";
import { DownloadReportButtons } from "~/features/orders/common/components/DownloadReportButtons";
import { type ReportType } from "~/features/orderbook/api/downloadOrderbookReport";
import { downloadOrderbook, filterOrderbookRows } from "~/features/orderbook/orderbookFilters";
import { getOrderbookDataResults } from "~/features/orders/common/DownloadOrderHistoryService/DownloadOrderHistoryService";
import { useSearchSubscription } from "~/features/orders/common/hooks/useSearchSubscription";
import {
  type MultiAccountAccountData,
  MultiAccountSearchDataProvider,
} from "~/features/orders/orderList/components/OrderListSearchBox/MultiAccountSelect/MultiAccountSearchDataProvider";
import { useTranslation } from "react-i18next";
import { Alert } from "~/common/mui/Alert/Alert";
import { useAppConfig } from "~/app/config/useAppConfig";
import { PageElements } from "~/common/components/PageElements";
import { Stack } from "~/common/mui";
import { OrderQuoteNoResults } from "~/common/components/NoResultsSection/OrderQuoteNoResults";
import OrderbookResultsTable from "~/features/orderbook/components/OrderbookTable/OrderbookResultsTable";
import { toColumnPreferences } from "~/features/orderbook/components/OrderbookTable/OrderBookUtility";
import {
  OrderListFeedbackContextProvider,
  type FeedbackOptionCategory,
  type UserData,
} from "~/features/orders/orderDetails/hooks/useSendOrderFeedback/components/OrderListFeedbackContext";

type OrderbookPageProps = {
  initialColumnPrefs: orderColumnPreferences[];
  initialRows?: OrderbookRowItem[];
  initialLoading?: boolean;
  initialError?: boolean;
  disableSearch?: boolean;
};

export const OrderbookPage: FC<OrderbookPageProps> = ({
  initialColumnPrefs,
  initialRows,
  initialLoading = false,
  initialError = false,
  disableSearch = false,
}) => {
  const { t } = useTranslation();
  const [rows, setRows] = useState<OrderbookRowItem[]>(initialRows ?? []);
  const [isLoading, setIsLoading] = useState(initialLoading);
  const [isError, setIsError] = useState(initialError);
  const [lastSearchQuery, setLastSearchQuery] = useState<OrderbookSearchBoxData>();
  const searchBoxRef = useRef<OrderbookSearchBoxHandle>(null);
  const webservicesDisabled = useAppConfig()?.configuration.disableAllWS ?? false;
  const [columnPreferences, setColumnPreferences] = useState<orderColumnPreferences[]>(initialColumnPrefs);

  const search = useSearchSubscription(getOrderbookDataResults, {
    onBegin: () => {
      setIsLoading(true);
      setIsError(false);
      setRows([]);
    },
    onError: () => {
      setIsLoading(false);
      setIsError(true);
    },
    onNext: ({ orderbookData }) => {
      const columnPrefs = toColumnPreferences<orderColumnPreferences>(initialColumnPrefs);
      setColumnPreferences(columnPrefs);
      setRows((prev) => prev.concat(orderbookData));
    },
    onComplete: () => setIsLoading(false),
  });

  const onSubmitHandler = useCallback(
    (formData: OrderbookSearchBoxData) => {
      setLastSearchQuery(formData);
      if (disableSearch) {
        return;
      }
      search(formData);
    },
    [disableSearch, search],
  );

  const filteredRows = useMemo(() => filterOrderbookRows(rows, lastSearchQuery), [rows, lastSearchQuery]);

  const newResultCount = filteredRows.filter((row) => row.isNew || row.isUpdated).length;

  const showRows = !isError && filteredRows.length > 0;
  const disableInteractions = isLoading || webservicesDisabled;
  return (
    <Stack gap={3}>
      <Stack direction={"row"} sx={{ gap: 2, alignItems: "center" }}>
        <PageElements.Title>{t("page.abmonitor.title")}</PageElements.Title>
        <PageElements.Subtitle>
          <span>
            {t("page.abmonitor.resultTitle.total", {
              count: filteredRows.length,
            })}
          </span>
          <span> / </span>
          <span>{t("page.abmonitor.resultTitle.new", { count: newResultCount })}</span>
        </PageElements.Subtitle>
      </Stack>
      <OrderbookSearchBox ref={searchBoxRef} onSubmit={onSubmitHandler} disabled={disableInteractions}>
        <DownloadReportButtons
          onDownload={(type) => downloadOrderbook(type as unknown as ReportType, lastSearchQuery)()}
          disabled={disableInteractions}
          testIdPrefix="abmonitor"
          label={t("page.abmonitor.downloads.title")}
        />
      </OrderbookSearchBox>

      {isLoading && <Spinner />}

      {showRows && (
        <OrderbookResultsTable
          rows={filteredRows}
          disableSorting={disableInteractions}
          orderBookColumnPreferences={columnPreferences}
        />
      )}

      {!webservicesDisabled && !isLoading && isError && <Alert severity="error">{t("page.abmonitor.error")}</Alert>}

      {!isLoading && !isError && !filteredRows.length && (
        <OrderQuoteNoResults
          title={t("page.orderbook.noResults.title")}
          onReset={() => searchBoxRef.current?.reset()}
        />
      )}
    </Stack>
  );
};

export interface ABMonitorAccountsData {
  accounts: MultiAccountAccountData[];
}

export interface ABMonitorPageProps {
  accounts?: ABMonitorAccountsData;
  showUnconfirmedLinesFilter: boolean;
  orderBookPreferences: orderColumnPreferences[];
  userData: UserData;
  orderFeedbackOptions: FeedbackOptionCategory[];
}

export function createOrderbookPage(data: ABMonitorPageProps) {
  return (
    <MultiAccountSearchDataProvider
      accountsJson={data.accounts ? JSON.stringify(data.accounts) : undefined}
      showUnconfirmedLinesFilter={data.showUnconfirmedLinesFilter}
    >
      {/*
        TODO(0145): MultiAccountSearchDataProvider is shared with the still-unmigrated
        QuoteList factory, which still expects a JSON string. Re-stringify here until that
        migrates and the provider is switched to typed data.
      */}
      <OrderListFeedbackContextProvider
        value={{ userData: data.userData, orderFeedbackOptions: data.orderFeedbackOptions }}
      >
        <OrderbookPage initialColumnPrefs={data.orderBookPreferences} />
      </OrderListFeedbackContextProvider>
    </MultiAccountSearchDataProvider>
  );
}
